import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { usePersistedAuthStore } from '../store/usePersistedAuthStore';
import { useTenantStore } from '../store/useTenantStore';
import { useWebView } from '../integrations/webview';

interface RouteGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireTenant?: boolean;
  isPublic?: boolean;
}

export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requireAuth = true,
  requireTenant = true,
  isPublic = false,
}) => {
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  
  // WebView integration
  const { isWebView, isAuthenticated: webViewAuthenticated, isLoading: webViewLoading } = useWebView();
  
  // Handle hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  
  // Reset navigation state on location change
  useEffect(() => {
    setHasNavigated(false);
  }, [location.pathname]);
  
  // Only log significant route changes in development
  useEffect(() => {
    if (import.meta.env.DEV && hasNavigated) {
      console.log('RouteGuard navigation:', location.pathname);
    }
  }, [location.pathname, hasNavigated]);
  
  try {
   
    
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const shouldChangePassword = usePersistedAuthStore((state) => state.shouldChangePassword);
    
    // Use a more direct approach to avoid hydration issues
    const tenantId = useTenantStore((state) => state.tenantId);
    const serverRootAddress = useTenantStore((state) => state.serverRootAddress);
    const hasValidTenant = !!tenantId && !!serverRootAddress;
    
   
    
    // Don't check routes until hydrated
    if (!isHydrated || webViewLoading) {
      
      return null;
    }
    
    // If in WebView and authenticated, bypass normal auth checks
    if (isWebView && webViewAuthenticated) {
      return <>{children}</>;
    }

    // Prevent multiple navigations
    if (hasNavigated) {
      return <>{children}</>;
    }

    // Check tenant requirement (skip for WebView)
    if (requireTenant && !hasValidTenant && !isWebView) {
      // Don't redirect if we're already on org-code page
      if (location.pathname !== '/org-code') {
        setHasNavigated(true);
        return <Navigate to="/org-code" state={{ from: location }} replace />;
      }
    }

    // Check auth requirement (skip for WebView)
    if (requireAuth && !isAuthenticated && !isWebView) {
      // Don't redirect if we're already on login page
      if (location.pathname !== '/login') {
        setHasNavigated(true);
        return <Navigate to="/login" state={{ from: location }} replace />;
      }
    }

    // Check password reset requirement
    if (isAuthenticated && shouldChangePassword && location.pathname !== '/reset-password') {
      setHasNavigated(true);
      return <Navigate to="/reset-password" replace />;
    }

    // Redirect authenticated users away from public pages
    // Exception: Don't redirect from player page as it's meant to be accessible by both authenticated and unauthenticated users
    const isPlayerRoute = location.pathname.startsWith('/player/');
    
    // For public pages, only redirect if authenticated and not on a special route
    if (isPublic && isAuthenticated && !shouldChangePassword && !isPlayerRoute) {
      // Public pages that should redirect: /org-code, /login
      const publicPagesToRedirect = ['/org-code', '/login'];
      if (publicPagesToRedirect.includes(location.pathname)) {
        const targetPath = isWebView ? "/whiteboard" : "/library";
        if (import.meta.env.DEV) {
          console.log(`RouteGuard: Redirecting from public page ${location.pathname} to ${targetPath}`);
        }
        setHasNavigated(true);
        return <Navigate to={targetPath} replace />;
      }
    }

    return <>{children}</>;
  } catch (error) {
    console.error('RouteGuard error:', error);
    // In case of error, show the children (fail open for now)
    return <>{children}</>;
  }
};